import {
  EVENT_NAME,
  FORM_ERROR_NAME,
  FORM_ERROR_TYPE,
  FORM_NAME,
  JOURNEY_NAME,
  JOURNEY_STEP_NAME,
  LINK_NAME,
  LINK_PREFIX,
  PAGE_CHANNEL,
  PAGE_ERROR_CODE,
  PAGE_ERROR_NAME,
  PAGE_ERROR_TYPE,
  PAGE_NAME,
  PAGE_NAME_PREFIX,
  PAGE_SECTION,
  PAGE_SUB_DOMAIN,
  PAGE_TYPE,
  StaticVariables,
  VISITOR_CUSTOMER_TYPE,
} from 'src/features/common/types/tealium-service';
import { Address, Recipient } from 'src/features/common/types';
import { PaymentTypes } from 'src/features/common/types/application/payment';
import { sendLinkToTealium, sendViewToTealium } from 'src/features/common/services/tealium-service';
import {
  calculateCompletion,
  countNumFields,
  retrieveFieldKeys,
} from 'src/features/common/helpers';
import { CartItem } from '@vfs-digital-channels/ns-utils';
import { ProductList } from 'src/features/order/services/order-tealium-services';
import { getProductName } from 'src/features/dpo/helpers/tealium-service-helpers';

const staticVariables = (visitor_customer_type: VISITOR_CUSTOMER_TYPE): StaticVariables => {
  return {
    page_url: window.location.href,
    page_section: PAGE_SECTION.BUY_POS,
    page_channel: PAGE_CHANNEL.VODAPAY_BUY_POS,
    page_subdomain: PAGE_SUB_DOMAIN.BUY_POS,
    page_type: PAGE_TYPE.WEBSITE,
    visitor_customer_type: visitor_customer_type,
  };
};

const getProductListItems = (cart: CartItem[]) => {
  const filteredProducts: CartItem[] = cart.filter((cartItem) => !cartItem.isTenderType);
  const products: ProductList = { name: [], quantity: [], price: [], transactionTotal: 0 };

  filteredProducts.forEach((product) => {
    const price = product.price ? product.price / 100 : 0;

    products.name.push(getProductName(product.title.toString()));
    products.quantity.push(product.quantity.toString());
    products.price.push(price.toString());
    products.transactionTotal =
      price !== 0
        ? products.transactionTotal + price * product.quantity
        : products.transactionTotal;
  });

  return {
    product_name: products.name.length ? products.name : undefined,
    product_quantity: products.quantity.length ? products.quantity : undefined,
    product_price: products.price.length ? products.price : undefined,
    transaction_total: products.transactionTotal,
  };
};

export interface IDpoTealiumService {
  // choose-solution page
  chooseSolutionLanding(productName: string): void;
  clickOnBuyOrRent(productName: string, buyOption: string): void;
  onAddToCart(productName: string, buyOption: string): void;
  onChooseSolutionProceedToCheckout(
    displayedProduct: string,
    productNames: string,
    buyOption: string,
  ): void;
  onMoreVodapayPaymentSolutions(productName: string): void;
  clickOnRelatedProduct(productName: string, selectedRelatedProductName: string): void;
  clickOnProductTabs(productName: string, tabName: string): void;
  onCartView(displayedProduct: string, productNames: string): void;
  changesProductQuantityInCart(
    displayedProduct: string,
    productNames: string,
    addedProductName: string,
  ): void;
  removesProductsFromCartChooseSolution(
    displayedProduct: string,
    productNames: string[],
    removedProductName: string,
  ): void;

  // tender-types page
  onTenderTypesLanding(productNames: string): void;
  onTenderTypesCheckout(productNames: string, tenderTypesInCart: string[]): void;

  // your-details page
  onPersonalDetailsIdle(
    incompleteFields: string,
    formCompletionRate: string,
    productNames: string,
  ): void;
  onPersonalDetailsLanding(productNames: string): void;
  onPersonalDetailsActions(productNames: string): void;
  personalDetailsSubmitted(productNames: string, transactionId: string | undefined): void;
  onShowBusinessAddress(): void;
  onOtpModalView(productName: string): void;
  onOtpModalAction(productName: string, action: 'cancel' | 'continue'): void;
  onResendOtp(productName: string): void;
  onIncorrectOtp(productName: string): void;
  otpExpired(productName: string): void;
  onWebcamCheckerModalView(productName: string): void;
  onWebcamCheckerModalAction(
    productName: string,
    action: 'try again later' | 'webcam enabled' | 'close',
  ): void;

  // assign-devices page
  onAssignDevicesLanding(productName: string, transactionId: string | undefined): void;
  onAssignDevicesAddressActions(
    productName: string,
    action: 'change address' | 'my address list' | 'add new',
  ): void;
  onAssignDevicesDeviceActions(productName: string, action: 'grab it now' | 'deliver later'): void;
  onAssignDevicesNext(productName: string, deliveryOptions: string): void;
  onAssignDeliveryAddressModalView(productName: string): void;
  onDeliveryAddressFieldClick(productName: string): void;
  addProductOnAssignAddressModal(productName: string, addedProductName: string): void;
  removeProductOnAssignAddressModal(productName: string, removedProductName: string): void;
  onAssignAddressModalNext(productName: string): void;
  onReceiverDetailsModalView(productName: string): void;
  closeReceiverDetailsModal(
    initialValues: Recipient,
    values: Recipient,
    action: 'close' | 'back',
  ): void;
  onReceiverDetailsModalNext(productName: string): void;
  onAddDeliveryAddressModalView(productName: string): void;
  closeAddDeliveryAddressModal(initialValues: Address, values: Address): void;
  onAddDeliveryAddressNext(productName: string): void;
  onDeviceScannerModalView(productName: string): void;
  onDeviceScannerModalAction(productName: string, action: 'scan' | 'enter'): void;
  onAssignNameModalView(productName: string): void;
  onAddCardMachine(productName: string): void;
  onAssignNameModalDone(productName: string): void;
  onAssignNameModalDelete(productName: string): void;
  onDeleteConfirmModal(productName: string): void;
  onDeleteConfirmModalActions(productName: string, action: 'cancel' | 'delete card machine'): void;
  onScanFailedModalView(productName: string): void;
  onScanFailedModalViewActions(productName: string, action: 'retry' | 'enter manually'): void;
  onSerialNumberModalView(productName: string): void;
  onSerialNumberModalActions(productName: string): void;

  // order-summary (pre-submit) page
  orderPreSubmitSummaryLanding(productNames: string, transactionId: string | undefined): void;
  updateProductQuantity(productName: string, addedProductName: string): void;
  removesProductsFromCartOrderSummary(productName: string, removedProductName: string): void;
  onEditOrAddDeliveryAddress(productName: string, action: 'edit' | 'add'): void;
  onBackOrNextClick(
    productName: string,
    action: 'back' | 'continue to payment' | 'complete application',
    paymentPreference?: PaymentTypes,
  ): void;
  onPaymentOptionClick(productName: string, paymentType: PaymentTypes): void;
  onReserveFundsModalView(productName: string): void;
  onReserveFundsAction(productName: string, action: 'cancel' | 'reserve funds'): void;
  onSubmitApplicationModalView(productName: string): void;
  onSubmitApplicationAction(productName: string, action: 'cancel' | 'submit'): void;

  // order-summary (post-submit) page
  paymentFailureLanding(errorMessage: string, errorCode: string): void;
  onPaymentFailureRetry(productName: string): void;
  onSuccessPageView(cart: CartItem[], transactionId: string): void;

  // payment page
  paymentPageLanding(productName: string, transactionId: string | undefined): void;
  selectEmailOrSMS(productName: string, selectedOption: 'email' | 'sms'): void;
  paymentPageAction(
    productName: string,
    action: 'send payment request' | 'check payment status' | 'cancel payment',
  ): void;
  paymentCancelledModalView(productName: string): void;
  paymentCancelledAction(productName: string, action: 'exit' | 'try again'): void;
}

export default class DpoTealiumService implements IDpoTealiumService {
  private journeyName: JOURNEY_NAME;
  private visitorCustomerType: VISITOR_CUSTOMER_TYPE;

  constructor(journeyName: JOURNEY_NAME, visitorCustomerType: VISITOR_CUSTOMER_TYPE) {
    this.journeyName = journeyName;
    this.visitorCustomerType = visitorCustomerType;
  }

  /** ##########################
   *   Choose Solution page
   * ###########################
   */
  chooseSolutionLanding(productName: string): void {
    sendViewToTealium({
      event_name: [EVENT_NAME.PAGE_VIEW, EVENT_NAME.JOURNEY_START, EVENT_NAME.PRODUCT_VIEW],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.CHOOSE_SOLUTION}: ${productName}`,
      journey_name: this.journeyName,
      product_name: productName,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  clickOnBuyOrRent(productName: string, buyOption: string): void {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.CHOOSE_SOLUTION}: ${productName}`,
      link_name: `${LINK_PREFIX.BUY_POS}${LINK_NAME.CHOOSE_SOLUTION}: ${productName}: ${buyOption}`,
      product_name: productName,
      journey_name: this.journeyName,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  onAddToCart(productName: string, buyOption: string): void {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION, EVENT_NAME.CART_ADD],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.CHOOSE_SOLUTION}: ${productName}`,
      link_name: `${LINK_PREFIX.BUY_POS}${LINK_NAME.CHOOSE_SOLUTION}: ${productName}: ${buyOption}: add to cart`,
      product_name: productName,
      journey_name: this.journeyName,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  onChooseSolutionProceedToCheckout(
    displayedProduct: string,
    productNames: string,
    buyOption: string,
  ): void {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.CHOOSE_SOLUTION}: ${displayedProduct}`,
      link_name: `${LINK_PREFIX.BUY_POS}${LINK_NAME.CHOOSE_SOLUTION}: ${displayedProduct}: ${buyOption}: proceed to checkout`,
      product_name: productNames,
      journey_name: this.journeyName,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  onMoreVodapayPaymentSolutions(productName: string): void {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.CHOOSE_SOLUTION}: ${productName}`,
      link_name: `${LINK_PREFIX.BUY_POS}${LINK_NAME.CHOOSE_SOLUTION}: ${productName}: more payment solutions`,
      product_name: productName,
      journey_name: this.journeyName,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  clickOnRelatedProduct(productName: string, selectedRelatedProductName: string): void {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.CHOOSE_SOLUTION}: ${productName}`,
      link_name: `${LINK_PREFIX.BUY_POS}${LINK_NAME.CHOOSE_SOLUTION}: ${productName}: related products: ${selectedRelatedProductName}`,
      product_name: productName,
      journey_name: this.journeyName,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  clickOnProductTabs(productName: string, tabName: string): void {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.CHOOSE_SOLUTION}: ${productName}`,
      link_name: `${LINK_PREFIX.BUY_POS}${LINK_NAME.CHOOSE_SOLUTION}: ${productName}: ${tabName}`,
      product_name: productName,
      journey_name: this.journeyName,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  onCartView(displayedProduct: string, productNames: string): void {
    sendViewToTealium({
      event_name: [EVENT_NAME.PAGE_VIEW, EVENT_NAME.CART_VIEW],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.CHOOSE_SOLUTION}: ${displayedProduct}: cart view`,
      product_name: productNames,
      journey_name: this.journeyName,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  // addsMoreProductsToCart
  changesProductQuantityInCart(
    displayedProduct: string,
    productNames: string,
    addedProductName: string,
  ): void {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION, EVENT_NAME.CART_ADD],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.CHOOSE_SOLUTION}: ${displayedProduct}: cart view`,
      link_name: `${LINK_PREFIX.BUY_POS}${LINK_NAME.CHOOSE_SOLUTION}: ${displayedProduct}: cart view: ${addedProductName}: add`,
      product_name: productNames,
      journey_name: this.journeyName,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  removesProductsFromCartChooseSolution(
    displayedProduct: string,
    productNames: string[],
    removedProductName: string,
  ): void {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION, EVENT_NAME.CART_REMOVE],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.CHOOSE_SOLUTION}: ${displayedProduct}: cart view`,
      link_name: `${LINK_PREFIX.BUY_POS}${LINK_NAME.CHOOSE_SOLUTION}: ${displayedProduct}: cart view: ${removedProductName}: remove`,
      product_name: productNames,
      journey_name: this.journeyName,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  /** ##########################
   *   Tender Types page
   * ###########################
   */
  onTenderTypesLanding(productNames: string): void {
    sendViewToTealium({
      event_name: [EVENT_NAME.PAGE_VIEW],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.TENDER_TYPE}`,
      journey_name: this.journeyName,
      product_name: productNames,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  onTenderTypesCheckout(productNames: string, tenderTypesInCart: string[]): void {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.TENDER_TYPE}`,
      link_name: `${LINK_PREFIX.BUY_POS}${LINK_NAME.TENDER_TYPE}`,
      journey_name: this.journeyName,
      product_name: productNames,
      tender_type: tenderTypesInCart.join(', '),
      ...staticVariables(this.visitorCustomerType),
    });
  }

  /** ##########################
   *   Your Details page
   * ###########################
   */
  onPersonalDetailsIdle(
    incompleteFields: string,
    formCompletionRate: string,
    productNames: string,
  ): void {
    sendViewToTealium({
      event_name: [EVENT_NAME.PAGE_VIEW],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.PERSONAL_DETAILS}`,
      journey_name: this.journeyName,
      product_name: productNames,
      form_name: FORM_NAME.PERSONAL_DETAILS,
      form_completion_rate: `${formCompletionRate}%`,
      form_field_incomplete: incompleteFields,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  onPersonalDetailsLanding(productNames: string): void {
    sendViewToTealium({
      event_name: [EVENT_NAME.PAGE_VIEW, EVENT_NAME.FORM_START],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.PERSONAL_DETAILS}`,
      journey_name: this.journeyName,
      product_name: productNames,
      form_name: FORM_NAME.PERSONAL_DETAILS,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  onPersonalDetailsActions(productNames: string): void {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION, EVENT_NAME.FORM_END],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.PERSONAL_DETAILS}`,
      link_page_name: `${LINK_PREFIX.BUY_POS}${LINK_NAME.PERSONAL_DETAILS}`,
      link_id: `${LINK_PREFIX.BUY_POS}${LINK_NAME.PERSONAL_DETAILS}: checkout`,
      journey_name: this.journeyName,
      product_name: productNames,
      form_name: FORM_NAME.PERSONAL_DETAILS,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  personalDetailsSubmitted(productNames: string, transactionId: string | undefined): void {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.PERSONAL_DETAILS}`,
      link_page_name: `${LINK_PREFIX.BUY_POS}${LINK_NAME.PERSONAL_DETAILS}`,
      link_id: `${LINK_PREFIX.BUY_POS}${LINK_NAME.PERSONAL_DETAILS}: salesforce response`,
      journey_name: this.journeyName,
      product_name: productNames,
      transaction_id: transactionId,
      form_name: FORM_NAME.PERSONAL_DETAILS,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  onShowBusinessAddress(): void {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.PERSONAL_DETAILS}`,
      link_id: `${LINK_PREFIX.BUY_POS}${LINK_NAME.PERSONAL_DETAILS}: show business address`,
      journey_name: this.journeyName,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  onOtpModalView(productName: string): void {
    sendViewToTealium({
      event_name: [EVENT_NAME.PAGE_VIEW],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.PERSONAL_DETAILS}: customer otp`,
      journey_name: this.journeyName,
      product_name: productName,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  onOtpModalAction(productName: string, action: 'cancel' | 'continue'): void {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.PERSONAL_DETAILS}: customer otp`,
      link_name: `${LINK_PREFIX.BUY_POS}${LINK_NAME.PERSONAL_DETAILS}: customer otp: ${action}`,
      journey_name: this.journeyName,
      product_name: productName,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  onResendOtp(productName: string): void {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.PERSONAL_DETAILS}: customer otp`,
      link_name: `${LINK_PREFIX.BUY_POS}${LINK_NAME.PERSONAL_DETAILS}: customer otp: resend`,
      journey_name: this.journeyName,
      product_name: productName,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  onIncorrectOtp(productName: string): void {
    sendViewToTealium({
      event_name: [EVENT_NAME.FORM_ERROR],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.PERSONAL_DETAILS}: customer otp`,
      journey_name: this.journeyName,
      product_name: productName,
      form_error_name: FORM_ERROR_NAME.INCORRECT_OTP,
      form_error_type: FORM_ERROR_TYPE.USER_ERROR,
      form_error_code: 400,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  otpExpired(productName: string): void {
    sendViewToTealium({
      event_name: [EVENT_NAME.FORM_ERROR],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.PERSONAL_DETAILS}: customer otp`,
      journey_name: this.journeyName,
      product_name: productName,
      form_error_name: FORM_ERROR_NAME.EXPIRED_OTP,
      form_error_type: FORM_ERROR_TYPE.USER_ERROR,
      form_error_code: 400,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  onWebcamCheckerModalView(productName: string): void {
    sendViewToTealium({
      event_name: [EVENT_NAME.PAGE_VIEW],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.ENABLE_WEBCAM}`,
      journey_name: this.journeyName,
      product_name: productName,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  onWebcamCheckerModalAction(
    productName: string,
    action: 'try again later' | 'webcam enabled' | 'close',
  ): void {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.ENABLE_WEBCAM}`,
      link_name: `${LINK_PREFIX.BUY_POS}${LINK_NAME.ENABLE_WEBCAM}: ${action}`,
      journey_name: this.journeyName,
      product_name: productName,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  /** ##########################
   *   Assign Devices page
   * ###########################
   */
  onAssignDevicesLanding(productName: string, transactionId: string | undefined): void {
    sendViewToTealium({
      event_name: [EVENT_NAME.PAGE_VIEW],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.DEVICE_OFFER}`,
      journey_name: this.journeyName,
      product_name: productName,
      transaction_id: transactionId,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  onAssignDevicesAddressActions(
    productName: string,
    action: 'change address' | 'my address list' | 'add new',
  ): void {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.DEVICE_OFFER}`,
      link_name: `${LINK_PREFIX.BUY_POS}${LINK_NAME.DEVICE_OFFER}: ${action}`,
      journey_name: this.journeyName,
      product_name: productName,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  onAssignDevicesDeviceActions(productName: string, action: 'grab it now' | 'deliver later'): void {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.DEVICE_OFFER}`,
      link_name: `${LINK_PREFIX.BUY_POS}${LINK_NAME.DEVICE_OFFER}: ${action}`,
      journey_name: this.journeyName,
      product_name: productName,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  onAssignDevicesNext(productName: string, deliveryOptions: string): void {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.DEVICE_OFFER}`,
      link_name: `${LINK_PREFIX.BUY_POS}${LINK_NAME.DEVICE_OFFER}: ${deliveryOptions}: next`,
      journey_name: this.journeyName,
      product_name: productName,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  onAssignDeliveryAddressModalView(productName: string): void {
    sendViewToTealium({
      event_name: [EVENT_NAME.PAGE_VIEW],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.ASSIGN_DELIVERY_ADDRESS}: amount of devices`,
      journey_name: this.journeyName,
      product_name: productName,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  onDeliveryAddressFieldClick(productName: string): void {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.ADD_DELIVERY_ADDRESS}: amount of devices`,
      link_name: `${LINK_PREFIX.BUY_POS}${LINK_NAME.ADD_DELIVERY_ADDRESS}: amount of devices: street address`,
      journey_name: this.journeyName,
      product_name: productName,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  addProductOnAssignAddressModal(productName: string, addedProductName: string): void {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.ASSIGN_DELIVERY_ADDRESS}: amount of devices`,
      link_name: `${LINK_PREFIX.BUY_POS}${LINK_NAME.ASSIGN_DELIVERY_ADDRESS}: ${addedProductName}: add product`,
      journey_name: this.journeyName,
      product_name: productName,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  removeProductOnAssignAddressModal(productName: string, removedProductName: string): void {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.ASSIGN_DELIVERY_ADDRESS}: amount of devices`,
      link_name: `${LINK_PREFIX.BUY_POS}${LINK_NAME.ASSIGN_DELIVERY_ADDRESS}: ${removedProductName}: remove product`,
      journey_name: this.journeyName,
      product_name: productName,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  onAssignAddressModalNext(productName: string): void {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.ASSIGN_DELIVERY_ADDRESS}: amount of devices`,
      link_name: `${LINK_PREFIX.BUY_POS}${LINK_NAME.ASSIGN_DELIVERY_ADDRESS}: next`,
      journey_name: this.journeyName,
      product_name: productName,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  onReceiverDetailsModalView(productName: string): void {
    sendViewToTealium({
      event_name: [EVENT_NAME.PAGE_VIEW],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.CHANGE_DELIVERY_ADDRESS}: receiver details`,
      journey_name: this.journeyName,
      product_name: productName,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  closeReceiverDetailsModal(
    initialValues: Recipient,
    values: Recipient,
    action: 'close' | 'back',
  ): void {
    const fieldCount = countNumFields<Recipient>(initialValues, ['phone']);
    const incompleteFields = retrieveFieldKeys<Recipient>(values, ['phone']);
    const completion = calculateCompletion(fieldCount, incompleteFields.length);

    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.CHANGE_DELIVERY_ADDRESS}: receiver details`,
      link_name: `${LINK_PREFIX.BUY_POS}${LINK_NAME.CHANGE_DELIVERY_ADDRESS}: ${action}`,
      journey_name: this.journeyName,
      form_completion_rate: `${completion}%`,
      form_field_incomplete: completion === '100' ? '' : incompleteFields.join(' | '),
      ...staticVariables(this.visitorCustomerType),
    });
  }

  onReceiverDetailsModalNext(productName: string): void {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.CHANGE_DELIVERY_ADDRESS}: receiver details`,
      link_name: `${LINK_PREFIX.BUY_POS}${LINK_NAME.CHANGE_DELIVERY_ADDRESS}: receiver details: next`,
      journey_name: this.journeyName,
      product_name: productName,
      form_completion_rate: '100%',
      ...staticVariables(this.visitorCustomerType),
    });
  }

  onAddDeliveryAddressModalView(productName: string): void {
    sendViewToTealium({
      event_name: [EVENT_NAME.PAGE_VIEW, EVENT_NAME.FORM_START],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.ADD_DELIVERY_ADDRESS}`,
      journey_name: this.journeyName,
      product_name: productName,
      form_name: FORM_NAME.DELIVERY_ADDRESS,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  closeAddDeliveryAddressModal(initialValues: Address, values: Address): void {
    const excludedFields = ['id', 'country', 'recipient'];
    const fieldCount = countNumFields<Address>(initialValues, excludedFields);
    const incompleteFields = retrieveFieldKeys<Address>(values, excludedFields);
    const completion = calculateCompletion(fieldCount, incompleteFields.length);

    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.ADD_DELIVERY_ADDRESS}`,
      link_name: `${LINK_PREFIX.BUY_POS}${LINK_NAME.ADD_DELIVERY_ADDRESS}: close`,
      journey_name: this.journeyName,
      form_name: FORM_NAME.DELIVERY_ADDRESS,
      form_completion_rate: `${completion}%`,
      form_field_incomplete: completion === '100' ? '' : incompleteFields.join(' | '),
      ...staticVariables(this.visitorCustomerType),
    });
  }

  onAddDeliveryAddressNext(productName: string): void {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION, EVENT_NAME.FORM_END],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.ADD_DELIVERY_ADDRESS}`,
      link_name: `${LINK_PREFIX.BUY_POS}${LINK_NAME.ADD_DELIVERY_ADDRESS}: next`,
      form_completion_rate: '100%',
      form_name: FORM_NAME.DELIVERY_ADDRESS,
      product_name: productName,
      journey_name: this.journeyName,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  onDeviceScannerModalView(productName: string): void {
    sendViewToTealium({
      event_name: [EVENT_NAME.PAGE_VIEW],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.ENABLE_WEBCAM}: webcam`,
      journey_name: this.journeyName,
      product_name: productName,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  onDeviceScannerModalAction(productName: string, action: 'scan' | 'enter'): void {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.ENABLE_WEBCAM}: webcam`,
      link_name: `${LINK_PREFIX.BUY_POS}${LINK_NAME.ENABLE_WEBCAM}: webcam: ${action}`,
      journey_name: this.journeyName,
      product_name: productName,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  onAssignNameModalView(productName: string): void {
    sendViewToTealium({
      event_name: [EVENT_NAME.PAGE_VIEW],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.NAME_CARD_MACHINE}`,
      journey_name: this.journeyName,
      product_name: productName,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  onAddCardMachine(productName: string): void {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.NAME_CARD_MACHINE}`,
      link_name: `${LINK_PREFIX.BUY_POS}${LINK_NAME.NAME_CARD_MACHINE}: add new`,
      journey_name: this.journeyName,
      product_name: productName,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  onAssignNameModalDone(productName: string): void {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.NAME_CARD_MACHINE}`,
      link_name: `${LINK_PREFIX.BUY_POS}${LINK_NAME.NAME_CARD_MACHINE}: done`,
      journey_name: this.journeyName,
      product_name: productName,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  onAssignNameModalDelete(productName: string): void {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.NAME_CARD_MACHINE}`,
      link_name: `${LINK_PREFIX.BUY_POS}${LINK_NAME.NAME_CARD_MACHINE}: delete`,
      journey_name: this.journeyName,
      product_name: productName,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  onDeleteConfirmModal(productName: string): void {
    sendViewToTealium({
      event_name: [EVENT_NAME.PAGE_VIEW],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.DELETE_DEVICE}`,
      journey_name: this.journeyName,
      product_name: productName,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  onDeleteConfirmModalActions(productName: string, action: 'cancel' | 'delete card machine'): void {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.DELETE_DEVICE}`,
      link_name: `${LINK_PREFIX.BUY_POS}${LINK_NAME.DELETE_DEVICE}: ${action}`,
      journey_name: this.journeyName,
      product_name: productName,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  onScanFailedModalView(productName: string): void {
    sendViewToTealium({
      event_name: [EVENT_NAME.PAGE_VIEW, EVENT_NAME.PAGE_ERROR],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.ENABLE_WEBCAM}: webcam: error page`,
      journey_name: this.journeyName,
      product_name: productName,
      page_error_name: PAGE_ERROR_NAME.SCANNING_FAILED,
      page_error_type: PAGE_ERROR_TYPE.USER_ERROR, // or system error
      page_error_code: PAGE_ERROR_CODE.NA, // backend error code
      ...staticVariables(this.visitorCustomerType),
    });
  }

  onScanFailedModalViewActions(productName: string, action: 'retry' | 'enter manually'): void {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.ENABLE_WEBCAM}: webcam: error page`,
      link_name: `${LINK_PREFIX.BUY_POS}${LINK_NAME.ENABLE_WEBCAM}: webcam: ${action}`,
      journey_name: this.journeyName,
      product_name: productName,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  onSerialNumberModalView(productName: string): void {
    sendViewToTealium({
      event_name: [EVENT_NAME.PAGE_VIEW],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.ENABLE_WEBCAM}: barcode enter manually`,
      journey_name: this.journeyName,
      product_name: productName,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  onSerialNumberModalActions(productName: string): void {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.ENABLE_WEBCAM}: barcode enter manually`,
      link_name: `${LINK_PREFIX.BUY_POS}${LINK_NAME.ENABLE_WEBCAM}: barcode enter manually: continue`,
      journey_name: this.journeyName,
      product_name: productName,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  /** ##################################
   *   Order Summary (pre-submit) page
   * ###################################
   */
  orderPreSubmitSummaryLanding(productNames: string, transactionId: string | undefined): void {
    sendViewToTealium({
      event_name: [EVENT_NAME.PAGE_VIEW, EVENT_NAME.CHECKOUT_START, EVENT_NAME.JOURNEY_STEP],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.ORDER_SUMMARY}`,
      product_name: productNames,
      journey_name: this.journeyName,
      journey_step_name: JOURNEY_STEP_NAME.ORDER_SUMMARY,
      transaction_id: transactionId,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  // addsMoreProductsToCart
  updateProductQuantity(productName: string, addedProductName: string): void {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION, EVENT_NAME.CART_ADD],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.ORDER_SUMMARY}`,
      link_name: `${LINK_PREFIX.BUY_POS}${LINK_NAME.ORDER_SUMMARY}: ${addedProductName}: add`,
      product_name: productName,
      journey_name: this.journeyName,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  removesProductsFromCartOrderSummary(productName: string, removedProductName: string): void {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION, EVENT_NAME.CART_REMOVE],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.ORDER_SUMMARY}`,
      link_name: `${LINK_PREFIX.BUY_POS}${LINK_NAME.ORDER_SUMMARY}: ${removedProductName}: remove`,
      product_name: productName,
      journey_name: this.journeyName,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  onEditOrAddDeliveryAddress(productName: string, action: 'edit' | 'add'): void {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.ORDER_SUMMARY}`,
      link_name: `${LINK_PREFIX.BUY_POS}${LINK_NAME.ORDER_SUMMARY}: ${action}: delivery address`,
      product_name: productName,
      journey_name: this.journeyName,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  onBackOrNextClick(
    productName: string,
    action: 'back' | 'continue to payment' | 'complete application',
    paymentPreference?: PaymentTypes,
  ): void {
    paymentPreference
      ? sendLinkToTealium({
          event_name: [EVENT_NAME.UI_INTERACTION],
          page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.ORDER_SUMMARY}`,
          link_name: `${LINK_PREFIX.BUY_POS}${LINK_NAME.ORDER_SUMMARY}: ${action}`,
          product_name: productName,
          journey_name: this.journeyName,
          payment_preference: paymentPreference,
          ...staticVariables(this.visitorCustomerType),
        })
      : sendLinkToTealium({
          event_name: [EVENT_NAME.UI_INTERACTION],
          page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.ORDER_SUMMARY}`,
          link_name: `${LINK_PREFIX.BUY_POS}${LINK_NAME.ORDER_SUMMARY}: ${action}`,
          product_name: productName,
          journey_name: this.journeyName,
          ...staticVariables(this.visitorCustomerType),
        });
  }

  onPaymentOptionClick(productName: string, paymentType: PaymentTypes): void {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.ORDER_SUMMARY}`,
      link_name: `${LINK_PREFIX.BUY_POS}${LINK_NAME.ORDER_SUMMARY}: payment option: ${paymentType}`,
      product_name: productName,
      journey_name: this.journeyName,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  onReserveFundsModalView(productName: string): void {
    sendViewToTealium({
      event_name: [EVENT_NAME.PAGE_VIEW],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.ORDER_SUMMARY}: reserve funds`,
      product_name: productName,
      journey_name: this.journeyName,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  onReserveFundsAction(productName: string, action: 'cancel' | 'reserve funds'): void {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.ORDER_SUMMARY}: reserve funds`,
      link_name: `${LINK_PREFIX.BUY_POS}${LINK_NAME.ORDER_SUMMARY}: reserve funds: ${action}`,
      product_name: productName,
      journey_name: this.journeyName,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  onSubmitApplicationModalView(productName: string): void {
    sendViewToTealium({
      event_name: [EVENT_NAME.PAGE_VIEW],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.ORDER_SUMMARY}: submit application`,
      product_name: productName,
      journey_name: this.journeyName,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  onSubmitApplicationAction(productName: string, action: 'cancel' | 'submit'): void {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.ORDER_SUMMARY}: submit application`,
      link_name: `${LINK_PREFIX.BUY_POS}${LINK_NAME.ORDER_SUMMARY}: submit application: ${action}`,
      product_name: productName,
      journey_name: this.journeyName,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  /** ##################################
   *   Order Summary (post-submit) page
   * ###################################
   */
  paymentFailureLanding(errorMessage: string, errorCode: string): void {
    sendViewToTealium({
      event_name: [EVENT_NAME.PAGE_VIEW, EVENT_NAME.PAGE_ERROR],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.PAYMENT_FAILED}`,
      journey_name: this.journeyName,
      event_error_name: `transaction failed: ${errorMessage || ''}`,
      event_error_code: errorCode,
      event_error_type: PAGE_ERROR_TYPE.USER_ERROR, // verify user error
      ...staticVariables(this.visitorCustomerType),
    });
  }

  onPaymentFailureRetry(productName: string): void {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.PAYMENT_FAILED}`,
      link_name: `${LINK_PREFIX.BUY_POS}${LINK_NAME.PAYMENT_FAILED}: retry`,
      product_name: productName,
      journey_name: this.journeyName,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  onSuccessPageView(cart: CartItem[], transactionId: string): void {
    sendViewToTealium({
      event_name: [
        EVENT_NAME.PAGE_VIEW,
        EVENT_NAME.CHECKOUT_COMPLETE,
        EVENT_NAME.JOURNEY_END,
        EVENT_NAME.TRANSACTION_COMPLETE,
        EVENT_NAME.SMME_APPLICATION_COMPLETE,
        EVENT_NAME.PRO_ONBOARDING_COMPLETE,
      ],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.ORDER_COMPLETE}`,
      journey_name: this.journeyName,
      product_category: 'POS',
      transaction_id: transactionId,
      ...getProductListItems(cart),
      ...staticVariables(this.visitorCustomerType),
    });
  }

  /** ##########################
   *   Payment page
   * ###########################
   */
  paymentPageLanding(productName: string, transactionId: string | undefined): void {
    sendViewToTealium({
      event_name: [EVENT_NAME.PAGE_VIEW],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.PAYMENT_REQUEST}: pay`,
      product_name: productName,
      journey_name: this.journeyName,
      transaction_id: transactionId,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  selectEmailOrSMS(productName: string, selectedOption: 'email' | 'sms'): void {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.PAYMENT_REQUEST}: pay`,
      link_name: `${LINK_PREFIX.BUY_POS}${LINK_NAME.PAYMENT_REQUEST}: ${selectedOption}`,
      product_name: productName,
      journey_name: this.journeyName,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  paymentPageAction(
    productName: string,
    action: 'send payment request' | 'check payment status' | 'cancel payment',
  ): void {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.PAYMENT_REQUEST}: pay`,
      link_name: `${LINK_PREFIX.BUY_POS}${LINK_NAME.PAYMENT_REQUEST}: ${action}`,
      product_name: productName,
      journey_name: this.journeyName,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  paymentCancelledModalView(productName: string): void {
    sendViewToTealium({
      event_name: [EVENT_NAME.PAGE_VIEW],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.PAYMENT_REQUEST}: pay: cancel`,
      product_name: productName,
      journey_name: this.journeyName,
      ...staticVariables(this.visitorCustomerType),
    });
  }

  paymentCancelledAction(productName: string, action: 'exit' | 'try again'): void {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.PAYMENT_REQUEST}: pay: cancel`,
      link_name: `${LINK_PREFIX.BUY_POS}${LINK_NAME.PAYMENT_REQUEST}: pay: cancel: ${action}`,
      product_name: productName,
      journey_name: this.journeyName,
      ...staticVariables(this.visitorCustomerType),
    });
  }
}
